
import React, { useEffect, useRef } from 'react';
import { Particle } from '../types';

interface ParticleSystemProps {
  particles: Particle[];
  setParticles: React.Dispatch<React.SetStateAction<Particle[]>>;
}

const ParticleSystem: React.FC<ParticleSystemProps> = ({ particles, setParticles }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;

    const render = () => {
      if (!canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (particles.length === 0) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      let hasChanged = false;
      const nextParticles: Particle[] = [];
      
      particles.forEach(p => {
        if (p.life <= 0) {
          hasChanged = true;
          return;
        }
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life;
        ctx.fill();
        
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.02;
        
        if (p.life > 0) {
          nextParticles.push(p);
        } else {
          hasChanged = true;
        }
      });

      if (hasChanged || nextParticles.length !== particles.length) {
        setParticles(nextParticles);
      }
      
      animationFrameId = requestAnimationFrame(render);
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [particles, setParticles]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-10" />;
};

export default ParticleSystem;
